import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgetCodeVerify from "./pages/ForgetCodeVerify";
import ForgotPassword from "./pages/ForgotPassword";
import CodeVerify from "./pages/CodeVerify";
import LandingFlow from "./pages/LandingFlow";
import BrandsSection from "./components/BrandsSection";
import SavedCarsSection from "./components/SavedCarsSection";
import FavoritePartsSection from "./components/FavoritePartsSection";
import GeneralHeader from "./components/GeneralHeader";
import Footer from "./components/Footer";
import MobileFooterSheet from "./components/MobileFooterSheet";
import Chat from "./pages/ChatPage";
import CartPage from "./pages/CartPage";
import NotificationsPage from "./pages/NotificationsPage";
import OrdersPage from "./pages/OrdersPage";
import MyAccountSection from "./components/MyAccountSection";
import PartDetailsWrapper from "./components/PartDetailsWrapper";
import AboutPage from "./pages/AboutPage";
import "sweetalert2/dist/sweetalert2.min.css";
import PaymentResult from "./pages/PaymentResult";
import { lsGet, lsSet, lsDel } from "./hooks/expiry";
import ViewportZoomController from "./controllers/ViewportZoomController";
import InAppBrowserBlocker from "./components/InAppBrowserBlocker";


const VIEWS = [
  "home",
  "login",
  "register",
  "forget",
  "reset",
  "verify",
  "brands",
  "chat",
  "savedCars",
  "favorites",
  "notifications",
  "orders",
  "account",
  "cart",
  "partDetails",
  "about",
];

const STORAGE_KEY = "currentView";
const BRANDS_PROGRESS_KEY = "BRANDS_STATE_V1"; // <— only this will be cleared
const ONE_DAY = 24 * 60 * 60 * 1000;

function getInitialView() {
  const saved = lsGet(STORAGE_KEY);
  return saved && VIEWS.includes(saved) ? saved : "home";
}

function parsePath(pathname) {
  // Payment result callback from PSP
  if (/^\/payment\/result\/?$/i.test(pathname)) {
    return { view: "paymentResult", params: {} };
  }
  // Accept "/part/123", "/part/uuid-xxx", with or without trailing slash
  const m = pathname.match(/^\/part\/([^/]+)\/?$/i);
  if (m) {
    return { view: "partDetails", params: { partId: decodeURIComponent(m[1]) } };
  }
  // Default entry view (your existing landing / brands flow)
  return { view: "brands", params: {} };
}

const goHome = () => {
  if (window.location.pathname !== "/") {
    window.history.pushState({}, "", "/");
    window.dispatchEvent(new PopStateEvent("popstate"));
  }
};

function App() {
  const [currentView, _setCurrentView] = React.useState(getInitialView);
  const [brandsMountKey, setBrandsMountKey] = React.useState(0);
  const [{ view, params }, setRoute] = useState(() => parsePath(window.location.pathname));

  // track last view so we can detect leaving "brands"
  const lastViewRef = React.useRef(getInitialView());

  // keep a responsive mobile flag
  const [isMobile, setIsMobile] = React.useState(
    () => window.matchMedia && window.matchMedia("(max-width: 768px)").matches
  );

  // keep a CSS variable --vh synced to the *real* viewport height (fixes iOS bars)
  // React.useEffect(() => {
  //   const setVh = () => {
  //     const vh = window.innerHeight * 0.01;
  //     document.documentElement.style.setProperty("--vh", `${vh}px`);
  //   };
  //   setVh();
  //   window.addEventListener("resize", setVh);
  //   return () => window.removeEventListener("resize", setVh);
  // }, []);

  // track media query changes (orientation / resize)
  // React.useEffect(() => {
  //   if (!window.matchMedia) return;
  //   const mq = window.matchMedia("(max-width: 768px)");
  //   const handler = (e) => setIsMobile(e.matches);
  //   mq.addEventListener?.("change", handler);
  //   // Safari fallback
  //   mq.addListenerz?.(handler);
  //   return () => {
  //     mq.removeEventListener?.("change", handler);
  //     mq.removeListener?.(handler);
  //   };
  // }, []);

  // when on Chat mobile: lock body scroll (safety) and add a class to root
  React.useEffect(() => {
    const root = document.querySelector(".view-wrapper");
    const shouldLock = currentView === "chat";

    if (shouldLock) {
      root?.classList.add("is-chat-mobile");
      document.body.style.setProperty("overflow", "hidden", "important");
    } else {
      root?.classList.remove("is-chat-mobile");
      document.body.style.overflow = "";
    }
    return () => {
      root?.classList.remove("is-chat-mobile");
      document.body.style.overflow = "";
    };
  }, [currentView, isMobile]);

  // Handle back/forward buttons
  useEffect(() => {
    const onPop = () => setRoute(parsePath(window.location.pathname));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Programmatic navigation
  const navigate = (path) => {
    if (path !== window.location.pathname) {
      window.history.pushState({}, "", path);
      setRoute(parsePath(path));
    }
  };

  // Open a part details page programmatically from anywhere
  const openPartById = (partId) => navigate(`/part/${partId}`);

  // Home / fallback (you can adapt to your own “home” path)
  const goHome = () => {
    setCurrentView("home"); // <- make the view switcher go to Home
    navigate("/"); // (optional) keep URL in sync
  };

  // Optionally: smarter back that prefers history.back()
  const goBackOrHome = () => {
    if (window.history.length > 1) window.history.back();
    else goHome();
  };

  // Wrap setCurrentView to also persist to localStorage (but not URL)
  const setCurrentView = React.useCallback((view) => {
    const safe = VIEWS.includes(view) ? view : "home";

    // if we are leaving "brands", wipe the saved wizard progress
    const leavingBrands = safe !== "brands" && safe != 'cart';
    if (leavingBrands) {
      try { lsDel(BRANDS_PROGRESS_KEY); } catch { }
    }

    // 👇 Force remount if we're navigating to Brands with a prefill present
    if (safe === "brands") {
      try {
        if (localStorage.getItem("BRANDS_PREFILL")) {
          setBrandsMountKey((k) => k + 1);
        }
      } catch { }
    }

    _setCurrentView(safe);
    lsSet(STORAGE_KEY, safe, ONE_DAY); // <-- TTL write
    lastViewRef.current = safe; // keep ref in sync
  }, []);

  // Optional: keep multiple tabs in sync via storage events
  React.useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) {
        const v = lsGet(STORAGE_KEY); // auto-clean if expired
        if (v && VIEWS.includes(v)) {
          _setCurrentView(v);
          lastViewRef.current = v;
        }
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // If a route-only view (e.g., /part/:id or /payment/result) takes over while we were on Brands,
  // clear saved Brands progress too.
  useEffect(() => {
    const routeTakesOver = view === "partDetails" || view === "paymentResult";
    if (lastViewRef.current === "brands" && routeTakesOver) {
      try {
        localStorage.removeItem(BRANDS_PROGRESS_KEY); // <-- ONLY this key
      } catch { }
    }
  }, [view]);
  const [zoomOverride, setZoomOverride] = useState(null);

  const allowZoom = currentView !== "chat";

  <ViewportZoomController allow={allowZoom} />

  const renderView = () => {
    switch (currentView) {
      case "login":
        return <LoginPage onBack={() => setCurrentView("home")} setCurrentView={setCurrentView} />;
      case "register":
        return <RegisterPage onBack={() => setCurrentView("login")} setCurrentView={setCurrentView} />;
      case "forget":
        return <ForgetCodeVerify onBack={() => setCurrentView("login")} setCurrentView={setCurrentView} />;
      case "reset":
        return <ForgotPassword onBack={() => setCurrentView("forget")} setCurrentView={setCurrentView} />;
      case "verify":
        return <CodeVerify onBack={() => setCurrentView("register")} setCurrentView={setCurrentView} />;
      case "brands":
        return (
          <>
            <BrandsSection
              key={brandsMountKey}
              onBack={() => setCurrentView("home")}
              onCartClick={() => setCurrentView("cart")}
              setCurrentView={setCurrentView}
              onGoHome={goHome}
              setZoomAllowed={setZoomOverride}
            />

            {/* Footers */}
            <div className="desktop-only" style={{  position: "absolute", top: "84%", right: "-2%" }}>
              <Footer setCurrentView={setCurrentView} />
            </div>
            {/* <div className="hero">
              <MobileFooterSheet />
            </div> */}
          </>

        );
      case "chat":
        return <Chat onBack={() => setCurrentView("home")} setCurrentView={setCurrentView} />;
      case "savedCars":
        return (
          <>
            <GeneralHeader currentView={currentView} setCurrentView={setCurrentView} />
            <SavedCarsSection setCurrentView={setCurrentView} onAddCar={() => setCurrentView("brands")} />
            <div className="desktop-only">
              <Footer setCurrentView={setCurrentView} />
            </div>
            <div className="hero">
              <MobileFooterSheet />
            </div>
          </>
        );
      case "favorites":
        return (
          <>
            <GeneralHeader currentView={currentView} setCurrentView={setCurrentView} />
            <FavoritePartsSection setCurrentView={setCurrentView} />
            <div className="desktop-only">
              <Footer setCurrentView={setCurrentView} />
            </div>
            <div className="hero">
              <MobileFooterSheet />
            </div>
          </>
        );
      case "notifications":
        return (
          <>
            <GeneralHeader currentView={currentView} setCurrentView={setCurrentView} />
            <NotificationsPage setCurrentView={setCurrentView} />
            <div className="desktop-only">
              <Footer setCurrentView={setCurrentView} />
            </div>
            <div className="hero">
              <MobileFooterSheet />
            </div>
          </>
        );
      case "orders":
        return (
          <>
            <GeneralHeader currentView={currentView} setCurrentView={setCurrentView} />
            <OrdersPage setCurrentView={setCurrentView} />
            <div className="desktop-only">
              <Footer setCurrentView={setCurrentView} />
            </div>
            <div className="hero">
              <MobileFooterSheet />
            </div>
          </>
        );
      case "account":
        return (
          <>
            <GeneralHeader currentView={currentView} setCurrentView={setCurrentView} />
            <MyAccountSection setCurrentView={setCurrentView} />
            <div className="desktop-only">
              <Footer setCurrentView={setCurrentView} />
            </div>
            <div className="hero">
              <MobileFooterSheet mode="sticky" />
            </div>
          </>
        );
      case "cart":
        return (
          <>
            <GeneralHeader currentView={currentView} setCurrentView={setCurrentView} />
            <CartPage setCurrentView={setCurrentView} />
          </>
        );
      case "about":
        return (
          <>
            <GeneralHeader currentView={currentView} setCurrentView={setCurrentView} />
            <AboutPage setCurrentView={setCurrentView} />
          </>
        );
      case "partDetails": {
        const storedId = sessionStorage.getItem("last_part_id");
        if (!storedId && !params?.partId) {
          return (
            <LandingFlow
              currentView={currentView}
              setCurrentView={setCurrentView}
              onRequestSelfOrder={() => setCurrentView("brands")}
            />
          );
        }
        return (
          <>
            <PartDetailsWrapper
              partId={params?.partId || storedId}
              onCartClick={() => { setCurrentView("cart"); navigate("/"); }}
              onLogoClick={goHome}
              setCurrentView={setCurrentView}
            />
            <div className="desktop-only"><Footer /></div>
          </>

        );
      }
      default:
        return (
          <LandingFlow
            currentView={currentView}
            setCurrentView={setCurrentView}
            onRequestSelfOrder={() => setCurrentView("brands")}
          />
        );
    }
  };

  if (view === "partDetails") {
    return (
      <>
        <div className="app dark" dir="rtl">
          {/* <InAppBrowserBlocker /> */}
          <ViewportZoomController allow={allowZoom} />
          <AnimatePresence mode="wait">
            <motion.div
              key={currentView}
              initial={{ x: 300, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -300, opacity: 0 }}
              transition={{ duration: 0.4, ease: "easeInOut" }}
              style={{ height: "100%" }}
              className="view-wrapper"
            >

              <PartDetailsWrapper
                partId={params.partId}
                onBack={goHome}
                onCartClick={() => { setCurrentView("cart"); navigate("/"); }}
                onLogoClick={goHome}
                setCurrentView={setCurrentView}
              />
              <div className="desktop-only"><Footer /></div>
              {/* <div className="hero">
                <MobileFooterSheet />
              </div> */}
            </motion.div>
          </AnimatePresence>
        </div>

      </>
    );
  }

  if (view === "paymentResult") {
    return (
      <>
        <ViewportZoomController allow={allowZoom} />
        <PaymentResult />
      </>
    );
  }

  return (
    <div className="app dark" dir="rtl">
      {/* <InAppBrowserBlocker /> */}
      <ViewportZoomController allow={allowZoom} />
      <AnimatePresence mode="wait">
        <motion.div
          key={currentView}
          initial={{ x: 300, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: -300, opacity: 0 }}
          transition={{ duration: 0.4, ease: "easeInOut" }}
          style={{ height: "100%" }}
          className="view-wrapper"
        >
          {renderView()}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

export default App;
